import { Link } from 'react-router';
import { Empty, EmptyDescription, EmptyHeader, EmptyTitle, Skeleton } from '@monksflow/monks-ui';
import { useTranslation } from 'react-i18next';
import { usePermissions } from '@/entities/user';
import {
  useExportFolderGuidelines,
  useFolder,
  useGuidelinesByFolder,
} from '@/features/guidelines/hooks';
import { DeleteConfirmationModal } from '@/shared/ui';
import { ROUTES } from '@/shared/config/routes';
import { GuidelinesDatatable } from '@/features/guidelines/ui/GuidelinesDatatable/GuidelinesDatatable';
import { GuidelineFormSheet } from '@/features/guidelines/ui/GuidelineFormSheet';
import { FolderIdentity } from './FolderIdentity';
import { FolderGuidelinesToolbar } from './FolderGuidelinesToolbar';
import { useFolderGuidelinesPane } from './hooks/useFolderGuidelinesPane';
import type { FolderGuidelinesPaneProps } from './FolderGuidelinesPane.types';

export function FolderGuidelinesPane({ folderId, onEditFolder }: FolderGuidelinesPaneProps) {
  const { t } = useTranslation('knowledge');

  const { canManageBrandVisualGuidelines } = usePermissions();

  const {
    data: folder,
    isPending: isFolderPending,
    isNotFound,
    isError,
  } = useFolder(folderId ?? undefined);
  const { data: guidelines, isPending: isGuidelinesPending } = useGuidelinesByFolder(
    folderId ?? undefined,
    { suppressErrorNotification: true },
  );
  const { mutate: exportGuidelines, isPending: isExporting } = useExportFolderGuidelines();

  const pane = useFolderGuidelinesPane({ guidelines: guidelines ?? [], folderId });
  const isFiltering = pane.searchQuery.trim().length > 0 || pane.selectedCategories.length > 0;

  const guidelineFormOpen = pane.dialogs.createOpen || pane.dialogs.guidelineToEdit !== null;
  // eslint-disable-next-line i18next/no-literal-string -- sheet mode tokens, not copy
  const guidelineFormMode = pane.dialogs.guidelineToEdit !== null ? 'edit' : 'create';
  const closeGuidelineForm =
    pane.dialogs.guidelineToEdit !== null ? pane.handlers.onCloseEdit : pane.handlers.onCloseCreate;

  if (folderId === null) {
    return (
      <Empty className="h-full flex-1">
        <EmptyHeader>
          <EmptyTitle>{t('guidelines.folderGuidelinesPane.selectAFolder')}</EmptyTitle>
        </EmptyHeader>
      </Empty>
    );
  }

  if (isFolderPending) {
    return (
      <div className="flex min-h-0 flex-1 flex-col gap-xl rounded-lg bg-card p-xl">
        <div className="flex items-start gap-md">
          <Skeleton className="size-14 shrink-0 rounded-sm" />
          <div className="flex min-w-0 flex-1 flex-col gap-3xs">
            <Skeleton className="h-8 w-64" />
            <Skeleton className="h-5 w-48" />
          </div>
        </div>
        <FolderGuidelinesToolbar
          searchQuery=""
          onSearchChange={() => {}}
          canManage={false}
          onAddGuideline={() => {}}
          hasGuidelines={false}
          isExporting={false}
          onExport={() => {}}
        />
        <GuidelinesDatatable
          guidelines={[]}
          isLoading
          sortBy="created"
          sortDirection="desc"
          selectedCategories={[]}
          onSort={() => {}}
          onCategoryFilterChange={() => {}}
          onEdit={() => {}}
          onDelete={() => {}}
          canManage={false}
        />
      </div>
    );
  }

  if (isNotFound || isError) {
    return (
      <Empty className="h-full flex-1">
        <EmptyHeader>
          <EmptyTitle>{t('guidelines.folderGuidelinesPane.folderUnavailable')}</EmptyTitle>
          <EmptyDescription>
            <Link to={ROUTES.KNOWLEDGE_HUB_VISUAL_GUIDELINES} className="text-primary underline">
              {t('guidelines.folderGuidelinesPane.backToFolders')}
            </Link>
          </EmptyDescription>
        </EmptyHeader>
      </Empty>
    );
  }

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-xl rounded-lg bg-card p-xl">
      <FolderIdentity
        title={folder?.title ?? ''}
        description={folder?.description}
        canEdit={canManageBrandVisualGuidelines}
        onEdit={onEditFolder}
        info={{ created: folder?.created, updated: folder?.updated }}
      />
      <FolderGuidelinesToolbar
        searchQuery={pane.searchQuery}
        onSearchChange={pane.handlers.onSearchChange}
        canManage={canManageBrandVisualGuidelines}
        onAddGuideline={pane.handlers.onOpenCreate}
        hasGuidelines={(guidelines?.length ?? 0) > 0}
        isExporting={isExporting}
        onExport={() => exportGuidelines({ folderId })}
      />
      <GuidelinesDatatable
        guidelines={pane.visibleGuidelines}
        isLoading={isGuidelinesPending}
        sortBy={pane.sortBy}
        sortDirection={pane.sortDirection}
        selectedCategories={pane.selectedCategories}
        onSort={pane.handlers.onSort}
        onCategoryFilterChange={pane.handlers.onCategoryFilterChange}
        onEdit={pane.handlers.onEditRow}
        onDelete={pane.handlers.onDeleteRow}
        canManage={canManageBrandVisualGuidelines}
        isFiltering={isFiltering}
      />
      <GuidelineFormSheet
        // Remounts on every open so its internal `useState` fields re-read
        // `guideline` at mount time instead of keeping whatever the previous
        // open session left behind — the same mechanism `FolderFormSheet`
        // uses at the workspace shell.
        key={
          guidelineFormOpen
            ? `${guidelineFormMode}-${pane.dialogs.guidelineToEdit?.id ?? 'none'}`
            : 'closed'
        }
        open={guidelineFormOpen}
        mode={guidelineFormMode}
        guideline={pane.dialogs.guidelineToEdit}
        folderId={folderId}
        onClose={closeGuidelineForm}
      />
      <DeleteConfirmationModal
        open={pane.dialogs.guidelineToDelete !== null}
        isDeleting={pane.dialogs.isDeleting}
        onCancel={pane.handlers.onCancelDelete}
        onConfirmDelete={pane.handlers.onConfirmDelete}
        resourceType={t('folderGuidelines.guidelineResource')}
        resourceName={pane.dialogs.guidelineToDelete?.name}
        warningMessage={t('folderGuidelines.thisActionCannotBeUndone')}
      />
    </div>
  );
}

FolderGuidelinesPane.displayName = 'FolderGuidelinesPane';
